import { Palette } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { GradientPreview } from "./GradientPreview";

interface BoundToningsProps {
  skuConfig: any;
  templateConfig: any;
  currentSkin: any;
  skuErrors: any[];
  onCleanToning: (skuId: string) => void;
}

export function BoundTonings({
  skuConfig,
  templateConfig,
  currentSkin,
  skuErrors,
  onCleanToning,
}: BoundToningsProps) {
  const toningIds = skuConfig.toningIds || [];
  const boundToningCount = toningIds.length;

  if (boundToningCount === 0) return null;

  const invalidToningError = skuErrors.find((e) => e.type === "invalid_toning");
  const duplicateToningError = skuErrors.find(
    (e) => e.type === "duplicate_toning",
  );
  const hasInvalidTonings = !!invalidToningError;
  const hasDuplicateTonings = !!duplicateToningError;
  const invalidToningCount = invalidToningError?.details?.length || 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <Palette className="h-3.5 w-3.5 text-muted-foreground" />
        <Label className="text-xs">已绑定调色方案</Label>
        <Badge variant="secondary" className="text-[10px]">
          {boundToningCount} 个
        </Badge>
        {(hasInvalidTonings || hasDuplicateTonings) && (
          <>
            {hasInvalidTonings && (
              <Badge variant="destructive" className="text-[10px]">
                ⚠️ {invalidToningCount} 个引用不存在
              </Badge>
            )}
            {hasDuplicateTonings && (
              <Badge variant="destructive" className="text-[10px]">
                ⚠️ 有重复绑定
              </Badge>
            )}
            <Button
              variant="destructive"
              size="sm"
              className="h-5 px-2 text-[10px]"
              onClick={() => {
                if (currentSkin) {
                  onCleanToning(currentSkin.skuId);
                }
              }}
            >
              清理
            </Button>
          </>
        )}
      </div>
      <div className="space-y-1.5">
        {templateConfig?.tonings
          ?.filter((t: any) => skuConfig.toningIds?.includes(t.id))
          .map((toning: any) => {
            // 获取第一个配色方案的颜色数组
            const firstColorSet = toning.colors?.[0];
            return (
              <div
                key={toning.id}
                className="flex items-center gap-2 p-2 rounded-lg border bg-muted/30"
              >
                <div className="w-12 h-6 rounded border overflow-hidden shrink-0">
                  {firstColorSet && (
                    <GradientPreview colorSet={firstColorSet} />
                  )}
                </div>
                <span className="text-sm font-medium truncate flex-1">
                  {toning.name || toning.id}
                </span>
                <Badge
                  variant={toning.pro > 0 ? "default" : "secondary"}
                  className={cn(
                    "text-[10px] h-4 px-1 shrink-0",
                    toning.pro > 0 && "bg-amber-500",
                  )}
                >
                  {toning.pro > 0 ? `¥${toning.pro}` : "免费"}
                </Badge>
              </div>
            );
          })}
      </div>
    </div>
  );
}
